import { Checkbox, CircularProgress } from "@material-ui/core";
import moment from "moment";
import React from "react";
import { FaRobot } from "react-icons/fa";
import { FormattedMessage } from "react-intl";
import styled from "styled-components";

import cuideoTheme, {
  COLOR_SUCCESS,
} from "../../../../../../containers/themes/defaultTheme";
import { StyledAddButton } from "../../../../components/ValuationPipelineButton/ValuationPipelineButtonSC";
import { CandidateStatusTag } from "../../../../features/candidate";
import {
  TPrefilterBotStatus,
  TPrefilterCandidate,
} from "../../types/contract.types";

const BOT_SKIPPED_ORANGE = "#f29339";

const BOT_STATUS_COLORS: Record<TPrefilterBotStatus, string> = {
  sent: COLOR_SUCCESS,
  skipped: BOT_SKIPPED_ORANGE,
  failed: cuideoTheme.palette.error.main,
};

const StyledCard = styled.div<{ hovered?: boolean }>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem 0.75rem 0.5rem;
  border: 2px solid
    ${({ hovered }) => (hovered ? cuideoTheme.palette.primary.main : "#e7e7e7")};
  border-radius: 0.5rem;
  color: ${cuideoTheme.palette.primary.main};
  font-size: 0.95rem;
  cursor: pointer;
  background-color: ${({ hovered }) => (hovered ? "#fbfbfb" : "transparent")};
  transition: border-color 0.15s ease, background-color 0.15s ease;

  & + & {
    margin-top: 0.5rem;
  }
`;

const StyledCheckbox = styled(Checkbox)`
  flex-shrink: 0;
  padding: 0.25rem;
  .MuiSvgIcon-root {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const StyledInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const StyledName = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
`;

const StyledBotIcon = styled.div<{ $color: string }>`
  display: flex;
  flex-shrink: 0;
  font-size: 1rem;
  color: ${({ $color }) => $color};
`;

const StyledSexo = styled.div`
  font-size: 0.8rem;
  color: #7d7d7d;
`;

const StyledLastActivity = styled.div`
  font-size: 0.8rem;
  color: #7d7d7d;
`;

const StyledMeta = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
`;

const StyledDistance = styled.span`
  font-size: 0.8rem;
  color: #7d7d7d;
  white-space: nowrap;
`;

// Mismo botón azul "Añadir a Contrato" que las tarjetas de la pestaña Añadir,
// adaptado al ancho de la columna derecha de la tarjeta del prefiltro.
const StyledAddToContractButton = styled(StyledAddButton)`
  min-width: 6.5rem;
  max-width: 7.5rem;
  min-height: 2.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.8125rem;
  box-shadow: none;

  &.Mui-disabled {
    background-color: ${cuideoTheme.palette.primary.light};
    color: ${cuideoTheme.palette.common.white};
    opacity: 0.5;
  }

  .MuiCircularProgress-root {
    color: ${cuideoTheme.palette.common.white};
  }
`;

interface IPrefilterCandidateCardProps {
  candidate: TPrefilterCandidate;
  isHovered?: boolean;
  isSelected?: boolean;
  showAddToContract?: boolean;
  isAddingToContract?: boolean;
  onClick?: () => void;
  onSelect?: () => void;
  onAddToContract?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

/**
 * Tarjeta específica del listado de prefiltro de un contrato.
 * Muestra el nombre completo del candidato (nombre + apellidos + apellidos2),
 * su sexo, su estado Cuideo, la última actividad y la distancia en km al
 * contrato dentro de un recuadro, aislada aquí para poder personalizarla solo
 * en esta sección. Se resalta al pasar el hover, sincronizado con el marcador
 * del candidato en el mapa. El checkbox de la izquierda marca la tarjeta como
 * seleccionada; quién está seleccionado y qué se hace con ello se gestiona
 * fuera, en la página del contrato. Si el bot ya se ha procesado para este
 * candidato, junto al nombre aparece su icono con el color del resultado.
 * Con `showAddToContract` muestra, como en la pestaña Añadir, el botón "Añadir
 * a Contrato" (ECO-1480); queda deshabilitado mientras se añade. Una vez
 * añadido, la página del contrato lo quita del prefiltro.
 */
const PrefilterCandidateCard: React.FC<IPrefilterCandidateCardProps> = ({
  candidate,
  isHovered,
  isSelected,
  showAddToContract,
  isAddingToContract,
  onClick,
  onSelect,
  onAddToContract,
  onMouseEnter,
  onMouseLeave,
}) => {
  const {
    id,
    nombre,
    apellidos,
    apellidos2,
    sexo,
    estado_cuideo: estadoCuideo,
    last_activity_time: lastActivityTime,
    distance_to_contract_km: distanceKm,
    bot_status: botStatus,
  } = candidate;
  const fullName = [nombre, apellidos, apellidos2].filter(Boolean).join(" ");
  const botStatusColor = botStatus ? BOT_STATUS_COLORS[botStatus] : undefined;

  return (
    <StyledCard
      hovered={isHovered}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <StyledCheckbox
        checked={!!isSelected}
        onChange={() => {
          onSelect?.();
        }}
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
        }}
        color="primary"
      />
      <StyledInfo>
        <StyledName>
          <span>{fullName}</span>
          {!!botStatusColor && (
            <StyledBotIcon $color={botStatusColor}>
              <FaRobot />
            </StyledBotIcon>
          )}
        </StyledName>
        {!!sexo && <StyledSexo>{sexo}</StyledSexo>}
        {!!lastActivityTime && (
          <StyledLastActivity>
            <FormattedMessage
              id="PrefilterCandidateCard.LastActivity"
              defaultMessage="Última actividad {hace}"
              values={{ hace: moment(lastActivityTime).fromNow() }}
            />
          </StyledLastActivity>
        )}
      </StyledInfo>
      <StyledMeta>
        {!!estadoCuideo && (
          <CandidateStatusTag candidateId={id} status={estadoCuideo} />
        )}
        {showAddToContract && (
          <StyledAddToContractButton
            className="MuiCuideo-AddToContractButton"
            variant="contained"
            disabled={!!isAddingToContract}
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
              onAddToContract?.();
            }}
          >
            {isAddingToContract ? (
              <CircularProgress size={14} thickness={6} />
            ) : (
              <FormattedMessage
                id="PrefilterCandidateCard.AddToContract"
                defaultMessage="Añadir a Contrato"
              />
            )}
          </StyledAddToContractButton>
        )}
        {typeof distanceKm === "number" && (
          <StyledDistance>{`${Math.round(distanceKm)} km`}</StyledDistance>
        )}
      </StyledMeta>
    </StyledCard>
  );
};

export default PrefilterCandidateCard;
